/**
 * personalization-state.js
 * Viori Personalizer — Phase 1: State Engine
 *
 * Single source of truth for the visitor's hair profile and session data.
 * Every section that reads or writes personalization data uses this module.
 * Depends on: personalization-schema.js (must load first).
 *
 * Public API:
 *   VioriState.getState(key?)          → value | full state object
 *   VioriState.setState(key, value)    → boolean (success)
 *   VioriState.resetState()            → void
 *   VioriState.setConsent(granted)     → void
 *   VioriState.hasConsent()            → boolean
 *   VioriState.getSessionId()          → string (UUID v4)
 *   VioriState.sanitize(str)           → string (XSS-safe for DOM injection)
 *   VioriState.isComplete()            → boolean
 *   VioriState.getPersonaChunkKey()    → string | null
 *   VioriState.getConcernChunkKey()    → string | null
 *   VioriState.on(event, fn)           → void
 *   VioriState.off(event, fn)          → void
 *   VioriState.emit(event, data)       → void
 *   VioriState.schema                  → STATE_SCHEMA reference
 *   VioriState.version                 → current schema version
 *
 * Storage: localStorage "viori_ps" (single JSON blob).
 * Session ID: localStorage "viori_sid" + cookie (90-day, SameSite=Lax).
 * Fallback: window.__vioriMem (in-memory, session-scoped, private browsing).
 * Spec refs: Section 3, 3a, 3b, 3j
 */

(function (root) {
  'use strict';

  // Schema injected by personalization-schema.js
  var STATE_SCHEMA    = root.VIORI_STATE_SCHEMA;
  var MIGRATIONS      = root.VIORI_MIGRATIONS;
  var CURRENT_VERSION = root.VIORI_SCHEMA_VERSION;

  if (!STATE_SCHEMA) {
    console.error('[VioriState] personalization-schema.js must load first.');
    return;
  }

  // ── Constants ──────────────────────────────────────────────────────────────
  var LS_KEY              = 'viori_ps';
  var SID_KEY             = 'viori_sid';
  var CONSENT_KEY         = 'viori_consent';
  var PRODUCTS_CAP        = 50;

  // Build alias → canonical key map once
  var ALIAS_MAP = {};
  for (var _k in STATE_SCHEMA) {
    if (STATE_SCHEMA[_k].alias) ALIAS_MAP[STATE_SCHEMA[_k].alias] = _k;
  }

  // ── Event Bus ──────────────────────────────────────────────────────────────
  var _listeners = {};

  function _emit(event, data) {
    var handlers = _listeners[event] || [];
    for (var i = 0; i < handlers.length; i++) {
      try { handlers[i](data); } catch (e) { /* isolate handler errors */ }
    }
    try {
      document.dispatchEvent(new CustomEvent('viori:' + event, { detail: data }));
    } catch (e) { /* IE/old Safari: skip */ }
  }

  function on(event, fn) {
    if (!_listeners[event]) _listeners[event] = [];
    _listeners[event].push(fn);
  }

  function off(event, fn) {
    if (!_listeners[event]) return;
    _listeners[event] = _listeners[event].filter(function (h) { return h !== fn; });
  }

  // ── Storage Abstraction ────────────────────────────────────────────────────
  // Wraps localStorage with try/catch; silently falls back to in-memory.
  // Spec Section 3a: private browsing fallback.
  if (!root.__vioriMem) root.__vioriMem = {};
  var _lsAvail = null;

  function _lsOk() {
    if (_lsAvail !== null) return _lsAvail;
    try {
      localStorage.setItem('__v_probe__', '1');
      localStorage.removeItem('__v_probe__');
      _lsAvail = true;
    } catch (e) {
      _lsAvail = false;
      _emit('ls_unavailable', { reason: e.message });
    }
    return _lsAvail;
  }

  function _lsGet(key) {
    try { return _lsOk() ? localStorage.getItem(key) : null; } catch (e) { return null; }
  }

  function _lsSet(key, val) {
    try { if (_lsOk()) { localStorage.setItem(key, val); return true; } } catch (e) { _lsAvail = false; }
    return false;
  }

  function _lsDel(key) {
    try { if (_lsOk()) localStorage.removeItem(key); } catch (e) { /* swallow */ }
  }

  // ── SessionStorage (page-navigation bridge) ────────────────────────────────
  // sessionStorage survives page navigation within the same tab but clears on
  // tab close — used as a middle-tier fallback when localStorage consent is not
  // yet granted. Functional state (hair profile) is not PII; storing it in
  // sessionStorage is GDPR-compliant and allows the homepage→quiz skip logic to
  // work before the visitor accepts the cookie banner.
  function _ssGet(key) {
    try { return sessionStorage.getItem(key); } catch (e) { return null; }
  }

  function _ssSet(key, val) {
    try { sessionStorage.setItem(key, val); return true; } catch (e) { return false; }
  }

  function _ssDel(key) {
    try { sessionStorage.removeItem(key); } catch (e) { /* swallow */ }
  }

  // ── GDPR Consent ───────────────────────────────────────────────────────────
  // Spec Section 3a: only write localStorage AFTER consent. Revoke clears it.
  var _consent = null;

  function _readConsent() {
    if (_consent !== null) return _consent;
    var stored = _lsGet(CONSENT_KEY);
    if (stored === 'true')  { _consent = true;  return true;  }
    if (stored === 'false') { _consent = false; return false; }
    // Integrate with Shopify customerPrivacy API
    try {
      if (root.Shopify && root.Shopify.customerPrivacy &&
          typeof root.Shopify.customerPrivacy.userCanBeTracked === 'function') {
        _consent = root.Shopify.customerPrivacy.userCanBeTracked();
        return _consent;
      }
    } catch (e) { /* ignore */ }
    _consent = false; // default: no consent until confirmed
    return false;
  }

  function setConsent(granted) {
    _consent = !!granted;
    try { _lsSet(CONSENT_KEY, String(_consent)); } catch (e) { /* ignore */ }
    if (!granted) {
      _lsDel(LS_KEY);                         // GDPR: wipe localStorage state
      _ssDel(LS_KEY);                         // GDPR: wipe sessionStorage state
      root.__vioriMem[LS_KEY] = null;         // force re-init from defaults
      _memState = null;
    }
    _emit('consent_changed', { granted: _consent });
  }

  function hasConsent() { return _readConsent(); }

  // ── State Builders ─────────────────────────────────────────────────────────
  function _buildDefault() {
    var s = {};
    for (var k in STATE_SCHEMA) {
      var def = STATE_SCHEMA[k].default;
      s[k] = Array.isArray(def) ? [] : def;
    }
    s.meta_stateVersion = CURRENT_VERSION;
    s.meta_createdAt    = Date.now();
    s.meta_updatedAt    = Date.now();
    return s;
  }

  function _migrate(stored) {
    var v = stored.meta_stateVersion || 0;
    if (v === CURRENT_VERSION) return stored; // fast path — zero overhead
    // Jump-table: direct migration function if available (spec Section 3a)
    var migrated = MIGRATIONS[v] ? MIGRATIONS[v](stored) : stored;
    // Chain for intermediate versions not in jump-table
    if (!MIGRATIONS[v]) {
      for (var i = v; i < CURRENT_VERSION; i++) {
        if (MIGRATIONS[i]) migrated = MIGRATIONS[i](migrated);
      }
    }
    migrated.meta_stateVersion = CURRENT_VERSION;
    migrated.meta_updatedAt    = Date.now();
    return migrated;
  }

  // ── State Load / Save ──────────────────────────────────────────────────────
  var _memState = null;

  function _load() {
    if (_memState) return _memState;
    var useMemory = !_readConsent() || !_lsOk();
    // When using memory mode, prefer sessionStorage (survives page navigation)
    // over window.__vioriMem (page-scoped). This is the key fix for the
    // homepage→quiz skip logic: state written on the homepage is found here
    // on the quiz page even before the visitor grants cookie consent.
    var raw = useMemory
      ? (_ssGet(LS_KEY) || root.__vioriMem[LS_KEY] || null)
      : _lsGet(LS_KEY);

    if (raw) {
      try {
        var parsed = JSON.parse(raw);
        if ((parsed.meta_stateVersion || 0) !== CURRENT_VERSION) {
          parsed = _migrate(parsed);
          _save(parsed);
        }
        _memState = parsed;
        return _memState;
      } catch (e) {
        _emit('state_parse_error', { error: e.message });
        _emit('state_reset', { reason: 'parse_error' });
        _memState = _buildDefault();
        _save(_memState);
        return _memState;
      }
    }

    _memState = _buildDefault();
    _save(_memState);
    return _memState;
  }

  function _save(state) {
    var useMemory = !_readConsent() || !_lsOk();
    state.meta_lsUnavailable = useMemory;
    var json = JSON.stringify(state);
    if (useMemory || !_lsSet(LS_KEY, json)) {
      // Without full localStorage consent, bridge across page loads via
      // sessionStorage so homepage selections survive navigation to the quiz.
      if (useMemory) _ssSet(LS_KEY, json);
      root.__vioriMem[LS_KEY] = json;
    }
  }

  // ── XSS Sanitizer ─────────────────────────────────────────────────────────
  // Spec Section 3a: all state-driven DOM output must be sanitized.
  // Use element.textContent for DOM writes — only call sanitize() for
  // cases where innerHTML is unavoidable (e.g., attribute values).
  function sanitize(val) {
    if (val === null || val === undefined) return '';
    return String(val)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#x27;').replace(/\//g, '&#x2F;');
  }

  // ── Type Validator ─────────────────────────────────────────────────────────
  function _valid(canonKey, value) {
    var s = STATE_SCHEMA[canonKey];
    if (!s) return false;
    if (s.type === 'string') {
      if (typeof value !== 'string') return false;
      return !s.values || s.values.indexOf(value) !== -1;
    }
    if (s.type === 'string[]') {
      // browse_productsViewed accepts a single string (push-semantic) or full array
      if (canonKey === 'browse_productsViewed') {
        return typeof value === 'string' || Array.isArray(value);
      }
      if (!Array.isArray(value)) return false;
      if (s.values) return value.every(function (v) { return s.values.indexOf(v) !== -1; });
      return true;
    }
    if (s.type === 'boolean') return typeof value === 'boolean';
    if (s.type === 'number')  return typeof value === 'number';
    return true;
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  function getState(key) {
    var state = _load();
    if (!key) return state;
    var canon = ALIAS_MAP[key] || key;
    return state[canon] !== undefined ? state[canon] : null;
  }

  function setState(key, value) {
    var canon = ALIAS_MAP[key] || key;
    if (!STATE_SCHEMA[canon]) {
      _emit('state_error', { reason: 'unknown_key', key: key });
      return false;
    }
    var state = _load();
    // Null resets to default
    if (value === null) {
      var def = STATE_SCHEMA[canon].default;
      state[canon] = Array.isArray(def) ? [] : def;
      state.meta_updatedAt = Date.now();
      _save(state); _memState = state;
      _emit('state_change', { key: canon, value: null });
      return true;
    }
    if (!_valid(canon, value)) {
      _emit('state_error', { reason: 'type_mismatch', key: canon, value: value, expected: STATE_SCHEMA[canon].type });
      return false;
    }
    // productsViewed: push-semantic — accepts a single handle string (FIFO append)
    // or a full array (for bulk reset). Spec Section 3a: cap at 50 via FIFO eviction.
    if (canon === 'browse_productsViewed') {
      if (typeof value === 'string') {
        // Push-semantic: append one handle
        var arr = state[canon] || [];
        if (arr.indexOf(value) === -1) {
          arr.push(value);
          if (arr.length > PRODUCTS_CAP) arr.shift();
        }
        state[canon] = arr;
      } else if (Array.isArray(value)) {
        // Bulk-replace: cap to last 50 if needed
        state[canon] = value.slice(-PRODUCTS_CAP);
      }
    } else {
      state[canon] = value;
    }
    state.meta_updatedAt = Date.now();
    _save(state); _memState = state;
    _emit('state_change', { key: canon, alias: key, value: value });
    return true;
  }

  function resetState() {
    _memState = _buildDefault();
    _lsDel(LS_KEY);
    root.__vioriMem[LS_KEY] = null;
    _save(_memState);
    _emit('state_reset', { reason: 'explicit' });
  }

  function isComplete() { return getState('quiz_quizComplete') === true; }

  // Returns the Shopify asset filename stem for the visitor's hair type chunk.
  // Liquid: <script src="{{ 'reviews-' | append: VioriState.getPersonaChunkKey() | append: '.json' | asset_url }}">
  function getPersonaChunkKey() {
    var map = { oily: 'hairtype-oily', normal: 'hairtype-normal',
                dry: 'hairtype-dry', dryScalpCondition: 'hairtype-dryscalp' };
    return map[getState('hairType')] || null;
  }

  // Returns first-priority concern chunk key (caller merges with persona chunk client-side).
  function getConcernChunkKey() {
    var concerns = getState('hairConcerns') || [];
    var order    = ['thinning','dandruff','sensitiveScalp','colorTreated','flatHair','damagedFrizzy','noneHealthyHair'];
    var slugs    = { thinning:'concern-thinning', dandruff:'concern-dandruff',
                     sensitiveScalp:'concern-sensitivescalp', colorTreated:'concern-colortreated',
                     flatHair:'concern-flathair', damagedFrizzy:'concern-damagedfrizzy',
                     noneHealthyHair:'concern-healthy' };
    for (var i = 0; i < order.length; i++) {
      if (concerns.indexOf(order[i]) !== -1) return slugs[order[i]];
    }
    return null;
  }

  // ── Session ID ─────────────────────────────────────────────────────────────
  function _uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
  }

  var _sid = null;
  function getSessionId() {
    if (_sid) return _sid;
    try {
      var m = document.cookie.match(/(?:^|;\s*)viori_sid=([^;]+)/);
      if (m) _sid = m[1];
    } catch (e) { /* ignore */ }
    _sid = _sid || _lsGet(SID_KEY) || _uuid();
    try {
      var exp = new Date(Date.now() + 90 * 864e5).toUTCString();
      document.cookie = 'viori_sid=' + _sid + '; expires=' + exp + '; path=/; SameSite=Lax';
    } catch (e) { /* ignore */ }
    _lsSet(SID_KEY, _sid);
    return _sid;
  }

  // ── Cart Attribute Persistence ─────────────────────────────────────────────
  // Spec §9d: before checkout, write full state to Shopify cart attributes so
  // the checkout extension can relay them to the server proxy for metafield writes.
  // Called: (a) when add-to-cart event fires, (b) on checkout link click.
  // Fire-and-forget: errors never block the cart/checkout flow.
  function _writeCartAttributes() {
    if (!hasConsent()) return;
    try {
      var s = getState(); // full state object
      var attrs = {};
      // Flatten each non-empty key with "viori_" prefix
      Object.keys(s).forEach(function (k) {
        var v = s[k];
        if (v === null || v === undefined || v === '') return;
        if (Array.isArray(v) && v.length === 0) return;
        attrs['viori_' + k] = Array.isArray(v) ? v.join(',') : String(v);
      });
      attrs['viori_session_id'] = getSessionId();
      // Shopify cart/update.js accepts {attributes: {key: value}}
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: attrs }),
        keepalive: true
      }).catch(function () { /* ignore — non-blocking */ });
    } catch (e) { /* ignore */ }
  }

  // ── UTM Capture ────────────────────────────────────────────────────────────
  // Spec: runs on ANY landing page. Only overwrites if param is present.
  function _captureUTM() {
    try {
      var p = new URLSearchParams(window.location.search);
      if (p.get('utm_source'))   setState('utm_source',   p.get('utm_source'));
      if (p.get('utm_medium'))   setState('utm_medium',   p.get('utm_medium'));
      if (p.get('utm_campaign')) setState('utm_campaign', p.get('utm_campaign'));
    } catch (e) { /* URLSearchParams unavailable */ }
  }

  // ── URL State Fallback ─────────────────────────────────────────────────────
  // Reads ?ht= (hairType) and ?wf= (whoIsThisFor) from the URL.
  // These params are written by the quiz's _buildRedirectUrl() when redirecting
  // to /collections/quiz-results. They act as a fallback when localStorage /
  // sessionStorage is unavailable (private mode, no consent, cross-device).
  // Only writes if state key is NOT already set — stored state wins.
  // Spec ref: Section 3 (state fallback chain).
  var VALID_HAIR_TYPES = ['oily', 'normal', 'dry', 'dryScalpCondition'];
  var VALID_WHO_FOR    = ['myself', 'mens', 'kids', 'pet', 'gift'];

  function _captureUrlStateFallback() {
    try {
      var p = new URLSearchParams(window.location.search);
      var ht = p.get('ht');
      var wf = p.get('wf');
      if (ht && VALID_HAIR_TYPES.indexOf(ht) !== -1 && !getState('hairType')) {
        setState('hairType', ht);
      }
      if (wf && VALID_WHO_FOR.indexOf(wf) !== -1 && !getState('whoIsThisFor')) {
        setState('whoIsThisFor', wf);
      }
    } catch (e) { /* URLSearchParams unavailable */ }
  }

  // ── Init ───────────────────────────────────────────────────────────────────
  function _init() {
    getSessionId();
    _load();
    _captureUTM();
    _captureUrlStateFallback();
    // Forward Shopify consent events
    try {
      document.addEventListener('viori:consent_changed', function (e) {
        if (e.detail && typeof e.detail.granted === 'boolean') setConsent(e.detail.granted);
      });
    } catch (e) { /* ignore */ }
  }

  _init();

  // Wire cart attribute writes on checkout intent and ATC
  try {
    // Add-to-cart: any form submit to /cart/add
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form && form.action && form.action.indexOf('/cart/add') !== -1) {
        _writeCartAttributes();
      }
    }, true);
    // Checkout links: anchor tags pointing to /checkout
    document.addEventListener('click', function (e) {
      var el = e.target && e.target.closest ? e.target.closest('a[href*="/checkout"]') : null;
      if (el) _writeCartAttributes();
    }, true);
  } catch (e) { /* ignore */ }

  // ── Public Interface ───────────────────────────────────────────────────────
  root.VioriState = {
    getState: getState, setState: setState, resetState: resetState,
    setConsent: setConsent, hasConsent: hasConsent, getSessionId: getSessionId,
    sanitize: sanitize, isComplete: isComplete,
    getPersonaChunkKey: getPersonaChunkKey, getConcernChunkKey: getConcernChunkKey,
    on: on, off: off, emit: _emit,
    writeCartAttributes: _writeCartAttributes,
    schema: STATE_SCHEMA, version: CURRENT_VERSION,
  };

}(window));
